import {
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect } from "react"

import AddChildren from "./AddChildren"
import AddChildrenStart from "./AddChildrenStart"
import CoachQuestionnaireDisclaimer from "./CoachQuestionnaireDisclaimer"
import CoachQuestionnaireSection from "./CoachQuestionnaireSection"
import CoachSignUp from "./CoachSignUp"
import CoachSignUpSkip from "./CoachSignUpSkip"
import LoadingIntro from "./LoadingIntro"
import PostAnswerScreen from "./PostAnswerScreen"
import QuizOrSkipChoice from "./QuizOrSkipChoice"
import useCoachOnboardingStore, {
  CoachOnboardingStep,
  isCoachFocus,
} from "./useCoachOnboardingStore"
// Wheel-of-Life screens (PR #878) — kept in the codebase but no longer
// reached in the default funnel after the revert. Re-add the imports +
// switch cases below to re-enable.
// import PersonalizedPlanReveal from "./wheelOfLife/PersonalizedPlanReveal"
// import StageTimeline from "./wheelOfLife/StageTimeline"
// import WheelGoalsScreen from "./wheelOfLife/WheelGoalsScreen"
// import WheelRatingScreen from "./wheelOfLife/WheelRatingScreen"
import OnboardingPageWrapper from "../../components/onboarding/onboardingPageWrapper/OnboardingPageWrapper"
import { BACKGROUND_COLORS } from "../../constants"
import { useCoachFunnelEntryAttribution } from "../../hooks/useCoachFunnelEntryAttribution"
import useMobile from "../../hooks/useMobile"
import { useCoachOnboardIntakeFormQuestionnaireQuery } from "../../networking/queries"
import { useRouter } from "../../util/router"
import { localStorageItems, sessionManager } from "../../util/storage"

const CoachOnboarding = () => {
  const isMobile = useMobile()
  const router = useRouter()
  useCoachOnboardIntakeFormQuestionnaireQuery()
  const { step, setStep, setFocus } = useCoachOnboardingStore()

  // Skip the loader if the parent has already seen it this session. Default
  // store step is LOADING_INTRO; we override on mount so a back-nav from
  // the choice screen doesn't re-trigger the 2.5s wait.
  useEffect(() => {
    if (step !== CoachOnboardingStep.LOADING_INTRO) return
    try {
      if (sessionStorage.getItem("coachOnboardingLoaderShown") === "true") {
        setStep(CoachOnboardingStep.QUIZ_OR_SKIP)
      }
    } catch {
      // sessionStorage may be unavailable — let the loader play.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cross-domain attribution: CONTACT pixels + Amplitude SHOW_WELCOME +
  // Google Ads START_QUIZ. Landing-page CTAs (jooncoach.com) now route
  // straight here, bypassing /coach-welcome — so the attribution events
  // that used to fire only on /coach-welcome are fired from this hook on
  // either entry route, with sessionStorage dedup to prevent double-firing
  // if the parent traverses both.
  useCoachFunnelEntryAttribution("onboarding")

  // Capture the branching-CTA entry-point tag from jooncoach.com.
  useEffect(() => {
    const incoming = router.query.focus
    if (isCoachFocus(incoming)) setFocus(incoming)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.focus])

  // Redirect logged-in users to coaching page
  useEffect(() => {
    if (sessionManager.getRefreshToken()) {
      localStorage.setItem(
        localStorageItems.lastSeenPaywall,
        new Date().toISOString()
      )
      router.push("/coaching")
    }
  }, [router])

  // Reset window scroll to top whenever the funnel step changes — the
  // OnboardingPageWrapper preserves scroll across step swaps by default,
  // which left the next question scrolled off-screen if the parent had
  // scrolled while reviewing the previous one. Per-screen scroll resets
  // for sub-steps (e.g. wheelDomainStep) live in their own components.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" })
  }, [step])

  const currentStep = () => {
    switch (step) {
      case CoachOnboardingStep.LOADING_INTRO:
        return <LoadingIntro />
      case CoachOnboardingStep.QUIZ_OR_SKIP:
        return <QuizOrSkipChoice />
      case CoachOnboardingStep.COACH_SIGN_UP_SKIP:
        return <CoachSignUpSkip />
      case CoachOnboardingStep.COACH_QUESTIONNAIRE_DISCLAIMER:
        return <CoachQuestionnaireDisclaimer />
      case CoachOnboardingStep.COACH_QUESTIONNAIRE:
        return (
          <FlexBox
            direction="column"
            style={{ padding: `0 ${SPACING.space4}`, paddingBottom: "10dvh" }}
            gap={SPACING.space4}
          >
            <CoachQuestionnaireSection />
          </FlexBox>
        )
      case CoachOnboardingStep.POST_ANSWER_SCREEN:
        return <PostAnswerScreen />
      case CoachOnboardingStep.ADD_CHILDREN_START:
        return <AddChildrenStart />
      case CoachOnboardingStep.ADD_CHILDREN:
        return <AddChildren />
      case CoachOnboardingStep.COACH_SIGN_UP:
        return <CoachSignUp />
      // Wheel-of-Life cases removed from the switch on revert — the enum
      // values and component files (src/pages/coachOnboarding/wheelOfLife/)
      // are kept so the flow can be reintroduced behind a flag later.
      // Fall through to questionnaire if any stale state lands here.
      default:
        return (
          <FlexBox
            direction="column"
            style={{ padding: `0 ${SPACING.space4}`, paddingBottom: "10dvh" }}
            gap={SPACING.space4}
          >
            <CoachQuestionnaireSection />
          </FlexBox>
        )
    }
  }

  return (
    <OnboardingPageWrapper
      style={{
        background: isMobile
          ? "linear-gradient(to bottom, rgba(245, 250, 255, 1), rgba(210, 230, 250, 1))"
          : BACKGROUND_COLORS.xlightBlueBg,
      }}
    >
      <QuizPersistentHeader />
      {currentStep()}
    </OnboardingPageWrapper>
  )
}

// Persistent quiz header — sticks to the top of every onboarding step so the
// parent always sees the goal of the flow ("Let's find the coach for you").
// Inspired by Brad's feedback that strawberry.me reinforces the intent at
// the top of every step. Translucent + blurred so the background gradient
// still shows through.
function QuizPersistentHeader() {
  return (
    <FlexBox
      direction="row"
      align="center"
      justify="center"
      gap={SPACING.space2}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        width: "100%",
        padding: `${SPACING.space2} ${SPACING.space4}`,
        background: "rgba(255, 255, 255, 0.78)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderBottom: `1px solid ${JoonColorExpanded.neutral200}`,
      }}
    >
      <img
        src="/images/logos/logo_letter.png"
        alt=""
        aria-hidden="true"
        style={{ width: 22, height: 22, borderRadius: 6 }}
      />
      <Typography variant="bodySmallBold" color={JoonUIColor.text.primary}>
        Let&apos;s find the coach for you
      </Typography>
    </FlexBox>
  )
}

export default CoachOnboarding
